import React, { useCallback, useEffect, useState } from 'react';
import { Button, Checkbox, Input, Modal, message, Tooltip, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined, FilterOutlined, CloseOutlined } from '@ant-design/icons';
// import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import CommonTable from 'components/CommonTable';
import { getStaffList, updateStaffOverallStatus, resendTeamMemberInvite, IStaffListItem } from 'api/operation/group';
import { ERoleDisplayName } from 'types/enumItems';
import s from '../s.module.less';

const ROLE_CODE_DISPLAY: Record<string, string> = {
    GROUP_OWNER: ERoleDisplayName.GROUP_OWNER,
    GROUP_ADMINISTRATIVE_ASSISTANT: ERoleDisplayName.GROUP_ADMINISTRATIVE_ASSISTANT,
    GROUP_CLINICAL_ASSISTANT: ERoleDisplayName.GROUP_CLINICAL_ASSISTANT,
    GROUP_PRACTICE_MANAGER: ERoleDisplayName.GROUP_PRACTICE_MANAGER,
    GROUP_PROVIDER: ERoleDisplayName.GROUP_PROVIDER,
};

const STATUS_OPTIONS = [
    { value: 'ACTIVE', label: 'Active' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'DISABLED', label: 'Inactive' },
];

const STATUS_DISPLAY: Record<string, { cls: string; label: string }> = {
    ACTIVE: { cls: s.active, label: 'Active' },
    PENDING: { cls: s.pending, label: 'Pending' },
    DISABLED: { cls: s.inactive, label: 'Inactive' },
};

const ROLE_OPTIONS = [
    { value: 'GROUP_PRACTICE_MANAGER', label: 'Practice manager' },
    { value: 'GROUP_ADMINISTRATIVE_ASSISTANT', label: 'Administrative assistant' },
    { value: 'GROUP_CLINICAL_ASSISTANT', label: 'Clinical assistant (MA)' },
];

type ConfirmAction = 'Deactivate' | 'Reactivate' | 'Resend';

const MODAL_TITLES: Record<ConfirmAction, string> = {
    Deactivate: 'Deactivate team member',
    Reactivate: 'Reactivate team member',
    Resend: 'Resend invite',
};

const getModalTitle = (action?: ConfirmAction) => (action ? MODAL_TITLES[action] : '');
const getModalButtonText = (action?: ConfirmAction) => (action === 'Resend' ? 'Resend' : action || '');

const StaffTable = () => {
    const [pendingNameFilter, setPendingNameFilter] = useState('');
    const [nameFilter, setNameFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState<string[]>([]);
    const [roleFilter, setRoleFilter] = useState<string[]>([]);
    const [current, setCurrent] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<IStaffListItem[]>([]);
    const [total, setTotal] = useState(0);
    const [confirmModal, setConfirmModal] = useState<{ action: ConfirmAction; member: IStaffListItem } | null>(null);
    const [actionLoading, setActionLoading] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getStaffList({
                current,
                size: pageSize,
                name: nameFilter || undefined,
                statuses: statusFilter.length > 0 ? statusFilter : undefined,
                roleCodes: roleFilter.length > 0 ? roleFilter : undefined,
            });
            if (res && !res.error) {
                setData(res.data?.data?.records || []);
                setTotal(res.data?.data?.total || 0);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [current, pageSize, nameFilter, statusFilter, roleFilter]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleConfirm = useCallback(async () => {
        if (!confirmModal) return;
        setActionLoading(true);
        try {
            const { action, member } = confirmModal;
            let res;
            if (action === 'Resend') {
                res = await resendTeamMemberInvite(member.orgId, member.memberId);
            } else {
                const status = action === 'Deactivate' ? 'DISABLED' : 'ACTIVE';
                res = await updateStaffOverallStatus(member.userId, { status });
            }
            if (res && !res.error) {
                message.success(`${action} successful`);
            } else {
                message.error(res?.error || `${action} failed`);
            }
            setConfirmModal(null);
            fetchData();
        } catch {
            message.error(`${confirmModal.action} failed`);
        } finally {
            setActionLoading(false);
        }
    }, [confirmModal, fetchData]);

    const columns: ColumnsType<IStaffListItem> = [
        {
            title: 'NAME',
            dataIndex: 'name',
            width: '260px',
            fixed: 'left',
            className: s.tableColumn,
            filterIcon: <SearchOutlined />,
            filterDropdown: ({ close }) => (
                <div style={{ padding: 16 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                        placeholder="Search name"
                        value={pendingNameFilter}
                        onChange={(e) => setPendingNameFilter(e.target.value)}
                        onPressEnter={() => {
                            setNameFilter(pendingNameFilter);
                            setCurrent(1);
                            close();
                        }}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => {
                                setNameFilter(pendingNameFilter);
                                setCurrent(1);
                                close();
                            }}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                setPendingNameFilter('');
                                setNameFilter('');
                                setCurrent(1);
                                close();
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            render: (_: string, row: IStaffListItem) => (
                <div className={s.nameWrap}>
                    <div className={s.name}>
                        <a
                            className={s.gpLink}
                            onClick={() =>
                                window.open(`/operation/group-practice/staff-detail/${row.userId}`, '_blank')
                            }
                        >
                            {row.name}
                        </a>
                        <div className={s.email}>{row.email}</div>
                    </div>
                </div>
            ),
        },
        {
            title: 'PRACTICES',
            dataIndex: 'practicesCount',
            width: '150px',
            className: s.tableColumn,
        },
        {
            title: 'GROUP PRACTICE',
            dataIndex: 'gpName',
            width: '220px',
            className: s.tableColumn,
            filterIcon: <FilterOutlined style={{ color: roleFilter.length > 0 ? '#F6891F' : undefined }} />,
            filterDropdown: ({ confirm }) => (
                <div className={s.filterDropdown} onKeyDown={(e) => e.stopPropagation()}>
                    <Checkbox.Group
                        value={roleFilter}
                        onChange={(vals) => {
                            setRoleFilter(vals as string[]);
                            setCurrent(1);
                            confirm({ closeDropdown: true });
                        }}
                    >
                        <div className={s.filterCheckbox}>
                            {ROLE_OPTIONS.map((opt) => (
                                <Checkbox key={opt.value} value={opt.value}>
                                    {opt.label}
                                </Checkbox>
                            ))}
                        </div>
                    </Checkbox.Group>
                </div>
            ),
            render: (_: string, row: IStaffListItem) => {
                const roleName = ROLE_CODE_DISPLAY[row.roleName] || row.roleName;
                const otherCount = row.otherPractices?.length || 0;
                return (
                    <div>
                        <a
                            className={s.gpLink}
                            onClick={() => {
                                if (row.orgId) {
                                    window.open(`/operation/group-practice-detail/${row.orgId}`, '_blank');
                                }
                            }}
                        >
                            {row.gpName}
                        </a>
                        <div>
                            <span className={s.roleTag}>{roleName}</span>
                            {row.isAdmin && <span className={s.adminTag}>Admin</span>}
                        </div>
                        {otherCount > 0 && (
                            <Tooltip title={row.otherPractices?.map((p) => p.gpName).join(', ')}>
                                <span style={{ fontSize: 12, color: '#6B7280', cursor: 'pointer' }}>
                                    +{otherCount} more
                                </span>
                            </Tooltip>
                        )}
                    </div>
                );
            },
        },
        // {
        //     title: 'ASSIGNED PROVIDERS',
        //     dataIndex: 'assignedProviders',
        //     width: '200px',
        //     className: s.tableColumn,
        //     render: (_: any, row: IStaffListItem) => {
        //         if (!row.assignedProviders || row.assignedProviders.all) return 'All providers';
        //         const ids = row.assignedProviders.providerIds;
        //         if (ids && ids.length > 0) {
        //             return `${ids.length} provider${ids.length > 1 ? 's' : ''}`;
        //         }
        //         return '-';
        //     },
        // },
        {
            title: 'STATUS',
            dataIndex: 'status',
            width: '150px',
            className: s.tableColumn,
            filterIcon: <FilterOutlined style={{ color: statusFilter.length > 0 ? '#F6891F' : undefined }} />,
            filterDropdown: ({ confirm }) => (
                <div className={s.filterDropdown} onKeyDown={(e) => e.stopPropagation()}>
                    <Checkbox.Group
                        value={statusFilter}
                        onChange={(vals) => {
                            setStatusFilter(vals as string[]);
                            setCurrent(1);
                            confirm({ closeDropdown: true });
                        }}
                    >
                        <div className={s.filterCheckbox}>
                            {STATUS_OPTIONS.map((opt) => (
                                <Checkbox key={opt.value} value={opt.value}>
                                    {opt.label}
                                </Checkbox>
                            ))}
                        </div>
                    </Checkbox.Group>
                </div>
            ),
            render: (status: string) => {
                const item = STATUS_DISPLAY[status];
                if (!item) return status;
                return <span className={`${s.statusBadge} ${item.cls}`}>{item.label}</span>;
            },
        },
        {
            title: 'LAST ACTIVE',
            dataIndex: 'lastActiveAt',
            width: '140px',
            className: s.tableColumn,
            render: (v: string | null) => (v ? dayjs(v).format('MM/DD/YYYY') : '—'),
        },
        {
            title: 'ACTION',
            width: '200px',
            fixed: 'right',
            className: s.tableColumn,
            render: (_: unknown, row: IStaffListItem) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                    {row.status === 'ACTIVE' && (
                        <span
                            className={s.actionRelist}
                            style={{ color: '#EF4444' }}
                            onClick={() => setConfirmModal({ action: 'Deactivate', member: row })}
                        >
                            Deactivate
                        </span>
                    )}
                    {/* {row.status === 'PENDING' && (
                        <span
                            className={s.actionRelist}
                            onClick={() => setConfirmModal({ action: 'Resend', member: row })}
                        >
                            Resend invite
                        </span>
                    )} */}
                    {row.status === 'DISABLED' && (
                        <span
                            className={s.actionRelist}
                            onClick={() => setConfirmModal({ action: 'Reactivate', member: row })}
                        >
                            Reactivate
                        </span>
                    )}
                    <span
                        className={s.actionView}
                        onClick={() => window.open(`/operation/group-practice/staff-detail/${row.userId}`, '_blank')}
                    >
                        View
                    </span>
                </div>
            ),
        },
    ];

    return (
        <>
            <CommonTable<IStaffListItem>
                rowKey="memberId"
                bordered
                loading={loading}
                columns={columns}
                data={data}
                pagination={{
                    current,
                    pageSize,
                    total,
                }}
                onChange={(pagination) => {
                    setCurrent(pagination?.current || 1);
                    setPageSize(pagination?.pageSize || 10);
                }}
                scroll={{ x: '1400' }}
            />

            <Modal
                open={!!confirmModal}
                onCancel={() => setConfirmModal(null)}
                title={getModalTitle(confirmModal?.action)}
                closeIcon={<CloseOutlined />}
                footer={
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                        <Button onClick={() => setConfirmModal(null)}>Cancel</Button>
                        <Button
                            type="primary"
                            danger={confirmModal?.action === 'Deactivate'}
                            loading={actionLoading}
                            onClick={handleConfirm}
                        >
                            {getModalButtonText(confirmModal?.action)}
                        </Button>
                    </div>
                }
                destroyOnClose
            >
                {confirmModal?.action === 'Deactivate' && (
                    <p style={{ fontSize: 14, color: '#4B5563' }}>
                        Are you sure you want to deactivate <strong>{confirmModal?.member.name}</strong>? They will lose
                        access to all {confirmModal?.member.practicesCount} managed practices.
                    </p>
                )}
                {confirmModal?.action === 'Reactivate' && (
                    <p style={{ fontSize: 14, color: '#4B5563' }}>
                        Reactivate <strong>{confirmModal?.member.name}</strong>? They will regain access to their
                        managed practices.
                    </p>
                )}
                {confirmModal?.action === 'Resend' && (
                    <p style={{ fontSize: 14, color: '#4B5563' }}>
                        Resend invitation to <strong>{confirmModal?.member.name}</strong> ({confirmModal?.member.email}
                        )?
                    </p>
                )}
            </Modal>
        </>
    );
};

export default StaffTable;
